import {
  careInsuranceLevelOptions,
  genderLabels,
  type Patient,
} from '../constants/patient';
import { formatBirthDateWithEra, formatPatientAgeYears } from './patientDateFormat';

/** 家族構成テキスト内のキーパーソン名の直後に（キーパーソン）を付与 */
export function formatFamilyForChartMemo(
  familyStructure: string | null | undefined,
  keyPerson: string | null | undefined,
): string {
  const family = familyStructure?.trim() ?? '';
  const kp = keyPerson?.trim() ?? '';
  if (!family && !kp) return '';
  if (!kp) return family;
  if (!family) return `${kp}（キーパーソン）`;
  if (family.includes(kp)) {
    return family.replace(kp, `${kp}（キーパーソン）`);
  }
  return `${family}、${kp}（キーパーソン）`;
}

function formatReferralLine(patient: Patient): string {
  const name = patient.referral_source?.name?.trim() ?? '';
  const phone = patient.referral_phone?.trim() ?? '';
  return [name, phone].filter(Boolean).join('、');
}

function formatNameLine(patient: Patient): string {
  const name = patient.name?.trim() ?? '';
  const kana = patient.name_kana?.trim() ?? '';
  if (name && kana) return `${name}（${kana}）様`;
  if (name) return `${name}様`;
  if (kana) return `（${kana}）様`;
  return '';
}

function formatBirthDateLine(dob: string | null): string {
  return formatBirthDateWithEra(dob, formatPatientAgeYears(dob)) ?? '';
}

function formatDiagnosesLine(diagnoses: string[]): string {
  return diagnoses.filter((d) => d.trim()).join('。');
}

function formatHomeCareLine(intervention: boolean | null): string {
  if (intervention === null) return '';
  return intervention ? 'あり' : 'なし';
}

function formatCareLevelLine(patient: Patient): string {
  const level = patient.care_insurance_level;
  if (!level) return '';
  return careInsuranceLevelOptions.find((o) => o.value === level)?.label ?? level;
}

function formatVisitNursingLine(patient: Patient): string {
  const vn = patient.visit_nursing;
  if (vn?.has_service === undefined) return '';
  if (!vn.has_service) return 'なし';
  return vn.office_name?.trim() || 'なし';
}

/** カルテメモ貼り付け用の患者サマリテキスト */
export function formatPatientChartMemoText(patient: Patient): string {
  const lines = [
    `紹介元：${formatReferralLine(patient)}`,
    `名前：${formatNameLine(patient)}`,
    `生年月日：${formatBirthDateLine(patient.date_of_birth)}`,
    `性別：${patient.gender ? (genderLabels[patient.gender] ?? patient.gender) : ''}`,
    `住所：${patient.address?.trim() ?? ''}`,
    `家族：${formatFamilyForChartMemo(patient.family_structure, patient.key_person)}`,
    `病歴：${formatDiagnosesLine(patient.diagnoses ?? [])}`,
    `居宅：${formatHomeCareLine(patient.home_care_intervention)}`,
    `介護：${formatCareLevelLine(patient)}`,
    `訪看：${formatVisitNursingLine(patient)}`,
    `経緯：${patient.request_background?.trim() ?? ''}`,
  ];
  return lines.join('\n');
}
